'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations, useLocale } from 'next-intl'
import { Search, Filter } from 'lucide-react'

interface Category {
  id: string
  name: string
}

interface SearchFiltersProps {
  categories: Category[]
  selectedCategory: string
  initialSearchTerm: string
}

export function SearchFilters({ 
  categories, 
  selectedCategory, 
  initialSearchTerm 
}: SearchFiltersProps) {
  const router = useRouter()
  const locale = useLocale()
  const t = useTranslations('productsClient')
  
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  
  const handleCategoryChange = (categoryId: string) => {
    // Update URL with new category
    const params = new URLSearchParams()
    if (categoryId !== 'all') {
      params.set('category', categoryId)
    }
    if (searchTerm) {
      params.set('search', searchTerm)
    }
    
    const queryString = params.toString()
    const newUrl = `/${locale}/products${queryString ? `?${queryString}` : ''}`
    router.push(newUrl)
  }
  
  const handleSearch = () => {
    // Update URL with search term
    const params = new URLSearchParams()
    if (selectedCategory !== 'all') {
      params.set('category', selectedCategory)
    }
    if (searchTerm) {
      params.set('search', searchTerm)
    }
    
    const queryString = params.toString()
    const newUrl = `/${locale}/products${queryString ? `?${queryString}` : ''}`
    router.push(newUrl)
  }
  
  return (
    <div className="p-6 bg-surface shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h2 className="text-2xl font-semibold text-text-main">{t('catalogTitle')}</h2>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-grow md:flex-grow-0 md:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-text-main" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('searchPlaceholder')}
              className="pl-10 pr-4 py-2 w-full rounded-md bg-surface-hover text-text-main placeholder-text-main/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
            />
          </div>
          
          <button 
            onClick={handleSearch}
            className="p-2 rounded-md bg-primary bg-opacity-30 text-text-main hover:bg-opacity-50 transition-colors shadow-sm"
            aria-label={t('search')}
          >
            <Search className="h-5 w-5" />
          </button>
          
          <button 
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="p-2 rounded-md bg-surface-hover text-text-main hover:bg-secondary hover:bg-opacity-20 transition-colors shadow-sm"
            aria-label={t('filter')}
          >
            <Filter className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Category filters */}
      <div className={`mt-4 transition-all duration-300 ease-in-out overflow-hidden ${isFilterOpen ? 'max-h-40' : 'max-h-0 md:max-h-40'}`}>
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              className={`px-4 py-1.5 text-sm rounded-full transition shadow-sm ${
                selectedCategory.toLowerCase() === category.id.toLowerCase()
                  ? 'bg-gradient-to-r from-primary-gradient-from to-primary-gradient-to text-surface font-bold text-shadow-md text-md'
                  : 'bg-surface-hover text-text-main hover:bg-secondary hover:bg-opacity-20'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
